import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Icon,
  Step,
  StepDescription,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
  useToken,
  VisuallyHidden,
} from "@chakra-ui/react";
import { useReducedMotion } from "../../theme/foundations/motion";
import { motion } from "framer-motion";
import { FiCheck, FiCheckCircle } from "react-icons/fi";

export interface WizardStep {
  key: string;
  title: string;
  description?: string;
}

export interface WizardTitleProps {
  /** e.g. "NEW APPLICATION" */
  eyebrow: string;
  /** e.g. "Register a branch" — the h2 that becomes the modal's accessible name. */
  title: string;
}

export interface WizardProgressProps {
  steps: WizardStep[];
  /** 0-based, the existing `activeStep` state. */
  activeStep: number;
  /** The existing `isStepSaved` state — fills the rail to the end of the step and triggers the pulse. */
  isStepSaved: boolean;
  /** Accessible name of the progress bar (default "Application progress"). */
  label?: string;
}

/** Humanised titles for the wizard step keys used by the two application modals. */
export const WIZARD_STEP_TITLES: Record<string, string> = {
  general: "General & licence",
  contacts: "Contacts",
  references: "Bank & suppliers",
  uploads: "Documents & signature",
};

/** mainSteps.map → WizardStep[] using WIZARD_STEP_TITLES (falls back to the key, capitalised). */
export const toWizardSteps = (keys: string[]): WizardStep[] =>
  keys.map((key) => ({ key, title: WIZARD_STEP_TITLES[key] ?? key.charAt(0).toUpperCase() + key.slice(1) }));

/**
 * Wizard modal title row (eyebrow + h2). Place inside <ModalHeader p={0} border="none"> on its own, so the dialog's
 * aria-labelledby resolves to just the title.
 * @example <ModalHeader p={0} border="none"><WizardTitle eyebrow="NEW APPLICATION" title="Register a branch" /></ModalHeader>
 */
export const WizardTitle: React.FC<WizardTitleProps> = ({ eyebrow, title }) => (
  <Box px={{ base: 5, md: 8 }} pt={{ base: 5, md: 7 }} pb={5}>
    <Text textStyle="label" color="text.muted" aria-hidden>
      {eyebrow}
    </Text>
    <Heading as="h2" size="lg" mt={1}>
      {title}
    </Heading>
  </Box>
);

/**
 * Sunken stepper band → progress rail that fills like a level bar (pulses once when the step is saved; turns leaf.100
 * with "All done — review and submit" on the final saved step). Render as a sibling between <ModalCloseButton /> and
 * <ModalBody>, never inside ModalHeader (it would pollute the dialog's accessible name).
 * @example <WizardProgress steps={toWizardSteps(mainSteps)} activeStep={activeStep} isStepSaved={isStepSaved} />
 */
export const WizardProgress: React.FC<WizardProgressProps> = ({ steps, activeStep, isStepSaved, label = "Application progress" }) => {
  const reduce = useReducedMotion();
  const [leaf] = useToken("colors", ["leaf.500"]);
  const count = Math.max(1, steps.length);
  const current = steps[Math.min(Math.max(0, activeStep), count - 1)];
  const isLast = activeStep >= count - 1;
  const done = isLast && isStepSaved;
  const progress = Math.min(100, ((activeStep + (isStepSaved ? 1 : 0.5)) / count) * 100);

  const prevSaved = useRef(isStepSaved);
  const [pulseKey, setPulseKey] = useState(0);
  useEffect(() => {
    if (isStepSaved && !prevSaved.current) setPulseKey((k) => k + 1);
    prevSaved.current = isStepSaved;
  }, [isStepSaved]);

  // The scroller is only a keyboard stop (and a named region) while the stepper actually overflows it.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (el) setOverflows(el.scrollWidth > el.clientWidth + 1);
  }, []);
  useEffect(() => {
    measure();
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [measure]);

  const captionText = done ? "All done — review and submit" : `Step ${activeStep + 1} of ${count} · ${current?.title ?? ""}`;

  return (
    <Box
      layerStyle="sunkenBand"
      bg={done ? "leaf.100" : "surface.sunken"}
      px={{ base: 5, md: 8 }}
      py={4}
      transition="background-color 360ms cubic-bezier(0.2,0.8,0.2,1)"
    >
      {/* Keyboard-reachable scroller when it overflows: on phones the stepper is wider than the band and its steps are not focusable. */}
      <Box
        ref={scrollerRef}
        overflowX="auto"
        pb={1}
        tabIndex={overflows ? 0 : undefined}
        role={overflows ? "region" : undefined}
        aria-label={overflows ? "Application steps" : undefined}
        _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
      >
        <Stepper index={activeStep} size="md" minW={{ base: `${count * 9}rem`, md: "auto" }}>
          {steps.map((step) => (
            <Step key={step.key}>
              <StepIndicator>
                <StepStatus complete={<Icon as={FiCheck} boxSize="1rem" />} incomplete={<StepNumber />} active={<StepNumber />} />
              </StepIndicator>
              <Box flexShrink={0}>
                <StepTitle>{step.title}</StepTitle>
                {step.description && <StepDescription>{step.description}</StepDescription>}
              </Box>
              <StepSeparator />
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box mt={4}>
        <Box
          role="progressbar"
          aria-label={label}
          aria-valuetext={captionText}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          h="6px"
          bg="sand.200"
          borderRadius="full"
          position="relative"
          overflow="hidden"
        >
          <motion.div
            style={{ position: "absolute", left: 0, top: 0, bottom: 0, background: leaf, borderRadius: 9999 }}
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 120, damping: 20 }}
          />
          {pulseKey > 0 && !reduce && (
            <motion.div
              key={pulseKey}
              aria-hidden
              style={{ position: "absolute", inset: 0, background: leaf, borderRadius: 9999, transformOrigin: "left center" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.6, 0], scaleX: [1, 1.02, 1] }}
              transition={{ duration: 0.5 }}
            />
          )}
        </Box>
        <Flex justify="flex-end" align="center" gap={1.5} mt={2} aria-hidden>
          {done && <Icon as={FiCheckCircle} color="leaf.700" boxSize="14px" />}
          <Text textStyle="caption" color={done ? "leaf.800" : "text.muted"}>
            {captionText}
          </Text>
        </Flex>
        <VisuallyHidden aria-live="polite">
          {done ? "All steps complete. Review and submit." : `Step ${activeStep + 1} of ${count}: ${current?.title ?? ""}`}
        </VisuallyHidden>
      </Box>
    </Box>
  );
};
